import "@components/ag-grid/styles/AgCharts.css";
import { GetMerchantSuccessRateResponseData } from "@services/models/Analytics/GetMerchantSuccessRate";
import { AgCharts } from "ag-charts-react";
import React, { useMemo } from "react";
import { formatDisplayDate } from "@utils/safeDate";
import { isPaysonicHost } from "src/config/deploymentConfig";
import ConstantFunctions from "../../utils/constants_functions";

type Props = {
  data: GetMerchantSuccessRateResponseData[];
};

const getRandomColor = () =>
  `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0")}`;

const predefinedColors = [
  "#3B4CDB",
  "#81D1F4",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#F97316",
  "#84CC16",
  "#06B6D4",
  "#6366F1",
  "#A855F7",
  "#D946EF",
  "#0EA5E9",
];

const MerchantSuccessRateChart: React.FC<Props> = ({ data }) => {
  const constantFunctions = useMemo(() => new ConstantFunctions(), []);
  const isPaysonic = isPaysonicHost();

  const { chartData, chartOptions } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], chartOptions: {} };
    }

    const merchants = [...new Set(data.map((d) => d.merchantName))];
    const dates = [...new Set(data.map((d) => d.slot))].sort();

    const processedData = dates.map((date) => {
      const dateFormatted = formatDisplayDate(date, "en-IN", {
        day: "numeric",
        month: "short",
      });

      const dataPoint: any = { date: dateFormatted, slot: date };

      merchants.forEach((merchant) => {
        const merchantEntry = data.find(
          (d) => d.merchantName === merchant && d.slot === date,
        );
        dataPoint[merchant] = merchantEntry
          ? parseFloat(merchantEntry.successRatePercent.toString())
          : null;
      });

      return dataPoint;
    });

    const series = merchants.map((merchant, index) => {
      const color =
        predefinedColors[index % predefinedColors.length] || getRandomColor();

      return {
        type: "line",
        xKey: "date",
        yKey: merchant,
        yName: isPaysonic
          ? constantFunctions.maskName(merchant || "")
          : merchant,
        stroke: color,
        marker: {
          fill: color,
          stroke: color,
          size: 4,
        },
        strokeWidth: 2,
        connectMissingData: true,
      };
    });

    const options = {
      data: processedData,
      series,
      axes: [
        {
          type: "category",
          position: "bottom",
          title: {
            enabled: false,
          },
          label: {
            fontSize: 10,
            color: "var(--text-base)",
            rotation: 0,
          },
          line: {
            color: "transparent",
          },
          tick: {
            color: "transparent",
          },
          gridLine: {
            enabled: false,
          },
        },
        {
          type: "number",
          position: "left",
          title: {
            enabled: false,
          },
          label: {
            fontSize: 10,
            color: "var(--text-base)",
            formatter: (params: any) => `${params.value}%`,
          },
          line: {
            color: "transparent",
          },
          tick: {
            color: "transparent",
          },
          gridLine: {
            enabled: false,
          },
          min: 0,
        },
      ],
      background: {
        fill: "transparent",
      },
      padding: {
        top: 20,
        bottom: 40,
      },
      legend: {
        enabled: true,
        position: "right",
        item: {
          marker: {
            shape: "square",
            size: 8,
          },
          label: {
            fontSize: 10,
            color: "var(--text-base)",
          },
        },
        spacing: 10,
      },
      tooltip: {
        enabled: true,
        class: "ag-chart-tooltip-custom",
      },
    } as any;

    return { chartData: processedData, chartOptions: options };
  }, [constantFunctions, data, isPaysonic]);

  if (!data || data.length === 0) return null;

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <AgCharts options={chartOptions} />
    </div>
  );
};

export default MerchantSuccessRateChart;
